import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	ViewChild,
} from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { AuthService } from './../../service/auth.service';
import { NzModalService } from 'ng-zorro-antd/modal';

type SbStilState = 'form' | 'completed';

@Component({
	selector: 'app-sb-stil',
	standalone: false,
	templateUrl: './sb-stil.component.html',
	styleUrl: './sb-stil.component.scss',
})
export class SbStilComponent {
	state: SbStilState = 'form';

	isSubmitting: boolean = false;

	completedCaseReference: string = '';
	completedCaseType: string = 'STIL';

	sickLeaveMedicalCertificateFile: File | null = null;

	stilForm = {
		firstName: '',
		lastName: '',
		sickLeaveDateFrom: '',
		sickLeaveDateTo: '',
		idNumber: '',
		emailAddress: '',
		cellPhoneNumber: '',
		popiaConsent: '',
		signatureData: '',
		signatureDate: new Date().toISOString().substring(0, 10),
	};

	constructor(
		private authService: AuthService,
		private modalService: NzModalService,
		private cdr: ChangeDetectorRef,
	) {}

	onSingleFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;

		if (!input.files || input.files.length === 0) {
			this.sickLeaveMedicalCertificateFile = null;
			return;
		}

		this.sickLeaveMedicalCertificateFile = input.files[0];
	}

	async submitStilForm(): Promise<void> {
		if (!this.sickLeaveMedicalCertificateFile) {
			this.modalService.error({
				nzTitle: 'Document Required',
				nzContent: 'Please upload the sick leave medical certificate.',
			});
			return;
		}

		if (!this.stilForm.signatureData) {
			this.modalService.error({
				nzTitle: 'Signature Required',
				nzContent: 'Please sign the form before submitting.',
			});
			return;
		}

		this.isSubmitting = true;

		try {
			const pdfFile = await this.generateStilPdf();

			const payload = {
				workflowLookupKey: 'stil',
				submittedByEmail: this.stilForm.emailAddress,
				formData: {
					...this.stilForm,
					assessmentType: 'STIL',
				},
			};

			const formData = new FormData();

			formData.append(
				'payload',
				new Blob([JSON.stringify(payload)], {
					type: 'application/json',
				}),
			);

			formData.append(
				'stilFormPdf',
				pdfFile,
				'Medical Certificate Evidence - STIL.pdf',
			);

			formData.append(
				'sickLeaveMedicalCertificate',
				this.sickLeaveMedicalCertificateFile,
				this.sickLeaveMedicalCertificateFile.name,
			);

			this.authService.doSubmitSbStil(formData).subscribe({
				next: (result: any) => {
					this.isSubmitting = false;

					if (result.success === false) {
						this.modalService.error({
							nzTitle: 'Submission Failed',
							nzContent:
								result.message ||
								'The STIL submission could not be completed.',
						});
						return;
					}

					const responseData = result.data || {};

					this.completedCaseReference =
						responseData.ticketReference ||
						responseData.caseReference ||
						'';

					this.completedCaseType = 'STIL';

					this.state = 'completed';
				},
				error: () => {
					this.isSubmitting = false;

					this.modalService.error({
						nzTitle: 'Submission Failed',
						nzContent:
							'The STIL submission could not be completed.',
					});
				},
			});
		} catch (error) {
			console.error('STIL PDF generation failed:', error);

			this.isSubmitting = false;

			this.modalService.error({
				nzTitle: 'PDF Error',
				nzContent: 'Could not generate the STIL form PDF.',
			});
		}
	}

	@ViewChild('stilPdfContent')
	stilPdfContent!: ElementRef<HTMLDivElement>;

	private async generateStilPdf(): Promise<File> {
		const element = this.stilPdfContent.nativeElement;

		const canvas = await html2canvas(element, {
			scale: 2,
			useCORS: true,
			backgroundColor: '#ffffff',
		});

		const imgData = canvas.toDataURL('image/png');

		const pdf = new jsPDF('p', 'mm', 'a4');

		const pdfWidth = pdf.internal.pageSize.getWidth();
		const pdfHeight = pdf.internal.pageSize.getHeight();

		const imgWidth = pdfWidth;
		const imgHeight = (canvas.height * imgWidth) / canvas.width;

		let heightLeft = imgHeight;
		let position = 0;

		pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
		heightLeft -= pdfHeight;

		while (heightLeft > 0) {
			position = heightLeft - imgHeight;
			pdf.addPage();
			pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
			heightLeft -= pdfHeight;
		}

		const blob = pdf.output('blob');

		return new File([blob], 'stil-medical-certificate-evidence.pdf', {
			type: 'application/pdf',
		});
	}

	@ViewChild('signatureCanvas')
	set signatureCanvas(canvas: ElementRef<HTMLCanvasElement> | undefined) {
		if (!canvas) {
			return;
		}

		this.signatureCanvasRef = canvas;

		setTimeout(() => {
			this.setupSignatureCanvas();
		});
	}

	private signatureCanvasRef?: ElementRef<HTMLCanvasElement>;
	private ctx?: CanvasRenderingContext2D;
	private isDrawing = false;

	private setupSignatureCanvas(): void {
		if (!this.signatureCanvasRef) {
			return;
		}

		const canvas = this.signatureCanvasRef.nativeElement;
		const rect = canvas.getBoundingClientRect();

		canvas.width = rect.width || 600;
		canvas.height = 180;

		const context = canvas.getContext('2d');

		if (!context) {
			return;
		}

		this.ctx = context;
		this.ctx.lineWidth = 2;
		this.ctx.lineCap = 'round';
		this.ctx.lineJoin = 'round';
		this.ctx.strokeStyle = '#000';
	}

	startDrawing(event: MouseEvent | TouchEvent): void {
		event.preventDefault();

		if (!this.ctx) {
			this.setupSignatureCanvas();
		}

		if (!this.ctx) {
			return;
		}

		const position = this.getCanvasPosition(event);

		this.isDrawing = true;
		this.ctx.beginPath();
		this.ctx.moveTo(position.x, position.y);
	}

	draw(event: MouseEvent | TouchEvent): void {
		if (!this.isDrawing || !this.ctx) {
			return;
		}

		event.preventDefault();

		const position = this.getCanvasPosition(event);

		this.ctx.lineTo(position.x, position.y);
		this.ctx.stroke();

		this.updateSignatureData();
	}

	stopDrawing(): void {
		if (!this.isDrawing) {
			return;
		}

		this.isDrawing = false;
		this.updateSignatureData();
	}

	clearSignature(): void {
		if (!this.signatureCanvasRef || !this.ctx) {
			return;
		}

		const canvas = this.signatureCanvasRef.nativeElement;

		this.ctx.clearRect(0, 0, canvas.width, canvas.height);
		this.stilForm.signatureData = '';
	}

	private updateSignatureData(): void {
		if (!this.signatureCanvasRef) {
			return;
		}

		const canvas = this.signatureCanvasRef.nativeElement;
		this.stilForm.signatureData = canvas.toDataURL('image/png');
	}

	private getCanvasPosition(event: MouseEvent | TouchEvent): {
		x: number;
		y: number;
	} {
		if (!this.signatureCanvasRef) {
			return { x: 0, y: 0 };
		}

		const canvas = this.signatureCanvasRef.nativeElement;
		const rect = canvas.getBoundingClientRect();

		let clientX: number;
		let clientY: number;

		if (event instanceof TouchEvent) {
			clientX =
				event.touches[0]?.clientX || event.changedTouches[0].clientX;
			clientY =
				event.touches[0]?.clientY || event.changedTouches[0].clientY;
		} else {
			clientX = event.clientX;
			clientY = event.clientY;
		}

		return {
			x: clientX - rect.left,
			y: clientY - rect.top,
		};
	}
}
